import React, { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch'
import Button from '../../ui/Button'
import QuestionCard from '../QuestionCard'
import { PENDING_PIN } from '../pendingPinErrors'
import EditIcon from '../../ui/icons/EditIcon'
import TrashIcon from '../../ui/icons/TrashIcon'
import ZoomInIcon from '../../ui/icons/ZoomInIcon'
import ZoomOutIcon from '../../ui/icons/ZoomOutIcon'

const ZOOM_PILL_CLASS = [
  'tw:flex tw:items-center tw:gap-2 tw:min-h-9 tw:px-3',
  'tw:rounded-lg! tw:border tw:border-gray-300 tw:bg-white'
].join(' ')
const TOOLBAR_BUTTON_CLASS = 'tw:min-h-9 tw:py-0!'
const ZOOM_BUTTON_CLASS = [
  'tw:border-0 tw:bg-transparent tw:p-0 tw:cursor-pointer tw:leading-none',
  'tw:text-xl tw:text-gray-800 tw:disabled:opacity-40 tw:disabled:cursor-not-allowed'
].join(' ')

const POS_X = 'pos_x'
const POS_Y = 'pos_y'
const POSITION = 'position'
const MARK_PINS = 'mark_pins'
const MARK_PINS_ATTRIBUTES = 'mark_pins_attributes'
const PLAN_BACKGROUND_URL = 'plan_background_url'
const MIN_SCALE = 1
const MAX_SCALE = 2
const ZOOM_LEVELS = [1, 1.1, 1.25, 1.5, 1.75, 2]
const ZOOM_EPSILON = 0.01
const PANNING_EXCLUDED = ['plan-pin']

const clamp = value => Math.min(1, Math.max(0, value))

const pinStyleFor = point => ({ left: `${point.posX * 100}%`, top: `${point.posY * 100}%` })

const pinFromMark = mark => {
  const pending = mark[MARK_PINS_ATTRIBUTES]
  const source = pending ? pending.filter(pin => !pin.markedForDestroy) : mark[MARK_PINS]
  const existing = source && source[0]
  if (!existing)
    return null
  return { id: existing.id, posX: existing[POS_X], posY: existing[POS_Y] }
}

const serverPinId = mark => mark[MARK_PINS]?.[0]?.id ?? null

// Zoom around the middle of the viewport, so the part of the plan being looked at stays put.
const zoomedPosition = (state, viewport, factor) => {
  const halfWidth = viewport.clientWidth / 2
  const halfHeight = viewport.clientHeight / 2
  return {
    positionX: halfWidth - (halfWidth - state.positionX) * factor,
    positionY: halfHeight - (halfHeight - state.positionY) * factor
  }
}

const PlanMarkField = ({ mark, readonly, errorMessages, onChange }) => {
  const transformRef = useRef(null)
  const viewportRef = useRef(null)
  const transformStateRef = useRef(null)
  const imageRef = useRef(null)
  const draggingRef = useRef(false)
  const originalPinIdRef = useRef(serverPinId(mark))
  const [pin, setPin] = useState(() => pinFromMark(mark))
  const [draftPin, setDraftPin] = useState(null)
  const [mode, setMode] = useState('view')
  const [scale, setScale] = useState(1)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  // The draft pin lives only here: clear the pending flag on unmount, or a folded question blocks the save.
  useEffect(() => () => onChangeRef.current({ [PENDING_PIN]: false }), [])

  const backgroundUrl = mark.question[PLAN_BACKGROUND_URL]
  const placing = mode === 'placing'
  const zoomPct = Math.round(scale * 100)

  const pointFromEvent = useCallback(event => {
    const bounds = imageRef.current.getBoundingClientRect()
    return {
      posX: clamp((event.clientX - bounds.left) / bounds.width),
      posY: clamp((event.clientY - bounds.top) / bounds.height)
    }
  }, [])

  const handleTransform = useCallback((_ref, state) => {
    transformStateRef.current = state
    setScale(state.scale)
  }, [])

  const zoomToLevel = useCallback(direction => {
    const ref = transformRef.current
    if (!ref)
      return
    const ascending = direction > 0
    const levels = ascending ? ZOOM_LEVELS : [...ZOOM_LEVELS].reverse()
    const target = levels.find(level =>
      ascending ? level > scale + ZOOM_EPSILON : level < scale - ZOOM_EPSILON)
    if (!target)
      return
    const { current } = transformStateRef
    const viewport = viewportRef.current
    if (!current || !viewport) {
      ref.centerView(target, 0)
      return
    }
    const { positionX, positionY } = zoomedPosition(current, viewport, target / current.scale)
    ref.setTransform(positionX, positionY, target, 0)
  }, [scale])

  const handleZoomOut = useCallback(() => zoomToLevel(-1), [zoomToLevel])
  const handleZoomIn = useCallback(() => zoomToLevel(1), [zoomToLevel])

  const handlePinDown = useCallback(event => {
    if (!placing)
      return
    event.preventDefault()
    event.currentTarget.setPointerCapture(event.pointerId)
    draggingRef.current = true
  }, [placing])

  const handlePinMove = useCallback(event => {
    if (draggingRef.current)
      setDraftPin(pointFromEvent(event))
  }, [pointFromEvent])

  const handlePinUp = useCallback(() => {
    draggingRef.current = false
  }, [])

  const handleAdd = useCallback(() => {
    setDraftPin({ posX: 0.5, posY: 0.5 })
    setMode('placing')
    onChange({ [PENDING_PIN]: true })
  }, [onChange])

  const handleEdit = useCallback(() => {
    setDraftPin(pin ? { posX: pin.posX, posY: pin.posY } : { posX: 0.5, posY: 0.5 })
    setMode('placing')
    onChange({ [PENDING_PIN]: true })
  }, [onChange, pin])

  const handleCancel = useCallback(() => {
    setDraftPin(null)
    setMode('view')
    onChange({ [PENDING_PIN]: false })
  }, [onChange])

  const handleValidate = useCallback(() => {
    const id = originalPinIdRef.current
    setPin({ id, posX: draftPin.posX, posY: draftPin.posY })
    setDraftPin(null)
    setMode('view')
    const payload = { [POS_X]: draftPin.posX, [POS_Y]: draftPin.posY, [POSITION]: 0 }
    if (id)
      payload.id = id
    onChange({ [MARK_PINS_ATTRIBUTES]: [payload], [PENDING_PIN]: false })
  }, [draftPin, onChange])

  const handleDelete = useCallback(() => {
    const id = originalPinIdRef.current
    const pins = id ? [{ id, markedForDestroy: true }] : []
    onChange({ [MARK_PINS_ATTRIBUTES]: pins, [PENDING_PIN]: false })
    setPin(null)
  }, [onChange])

  if (!backgroundUrl) {
    return (
      <QuestionCard errorMessages={errorMessages} question={mark.question}>
        <div className="text-muted small">Aucun plan disponible pour cette ressource.</div>
      </QuestionCard>
    )
  }

  const activePin = placing ? draftPin : pin

  const toolbar = (
    <div className="tw:flex tw:items-center tw:gap-2">
      <div className={ZOOM_PILL_CLASS}>
        <button
          aria-label="Réduire"
          className={ZOOM_BUTTON_CLASS}
          disabled={zoomPct <= MIN_SCALE * 100}
          type="button"
          onClick={handleZoomOut}
        >
          <ZoomOutIcon />
        </button>
        <span className="tw:text-sm tw:font-medium tw:text-gray-950 tw:tabular-nums">{`${zoomPct}%`}</span>
        <button
          aria-label="Agrandir"
          className={ZOOM_BUTTON_CLASS}
          disabled={zoomPct >= MAX_SCALE * 100}
          type="button"
          onClick={handleZoomIn}
        >
          <ZoomInIcon />
        </button>
      </div>
      {!readonly && placing ? (
        <>
          <Button className={TOOLBAR_BUTTON_CLASS} variant="neutral" onClick={handleCancel}>Annuler</Button>
          <Button className={TOOLBAR_BUTTON_CLASS} variant="primary" onClick={handleValidate}>Valider</Button>
        </>
      ) : null}
      {!readonly && !placing && pin ? (
        <>
          <Button
            className={TOOLBAR_BUTTON_CLASS}
            iconNode={<TrashIcon />}
            variant="outline-destructive"
            onClick={handleDelete}
          >
            Supprimer le point
          </Button>
          <Button
            className={TOOLBAR_BUTTON_CLASS}
            iconNode={<EditIcon />}
            variant="outline"
            onClick={handleEdit}
          >
            Modifier le point
          </Button>
        </>
      ) : null}
      {!readonly && !placing && !pin ? (
        <Button className={TOOLBAR_BUTTON_CLASS} icon="plus" variant="primary" onClick={handleAdd}>
          Ajouter un point
        </Button>
      ) : null}
    </div>
  )

  return (
    <QuestionCard errorMessages={errorMessages} headerAction={toolbar} question={mark.question}>
      <div
        ref={viewportRef}
        className={`plan-edit-viewport ${placing ? 'plan-edit-viewport--editing' : ''}`.trim()}
      >
        <TransformWrapper
          ref={transformRef}
          doubleClick={{ disabled: true }}
          initialScale={1}
          maxScale={MAX_SCALE}
          minScale={MIN_SCALE}
          panning={{ excluded: PANNING_EXCLUDED }}
          wheel={{ disabled: true }}
          centerOnInit
          onTransform={handleTransform}
        >
          <TransformComponent contentClass="plan-edit-content" wrapperClass="plan-edit-wrapper">
            <div className="plan-edit-figure" style={{ '--plan-inverse-scale': 1 / scale }}>
              <img
                ref={imageRef}
                alt="Plan de la ressource"
                draggable={false}
                src={backgroundUrl}
              />
              {activePin ? (
                <span
                  className={`plan-pin ${placing ? 'plan-pin--draggable' : ''}`.trim()}
                  style={pinStyleFor(activePin)}
                  onPointerCancel={handlePinUp}
                  onPointerDown={handlePinDown}
                  onPointerMove={handlePinMove}
                  onPointerUp={handlePinUp}
                />
              ) : null}
              {placing ? (
                <span className="plan-hint-badge" style={pinStyleFor(activePin)}>
                  Déplacez le pointeur
                  <br />
                  selon votre besoin
                </span>
              ) : null}
            </div>
          </TransformComponent>
        </TransformWrapper>
      </div>
    </QuestionCard>
  )
}

PlanMarkField.propTypes = {
  errorMessages: PropTypes.arrayOf(PropTypes.string),
  mark: PropTypes.shape({
    [MARK_PINS]: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      [POS_X]: PropTypes.number,
      [POS_Y]: PropTypes.number
    })),
    question: PropTypes.shape({
      mandatory: PropTypes.bool,
      [PLAN_BACKGROUND_URL]: PropTypes.string
    }).isRequired
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  readonly: PropTypes.bool
}

PlanMarkField.defaultProps = {
  errorMessages: [],
  readonly: false
}

export default PlanMarkField
